import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import '../styles/PaymentSuccess.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const paymentStatus = searchParams.get('status'); // 'success' or 'failed' from BoG
  const transactionId = searchParams.get('transactionId');
  const cart = useStore((state) => state.cart);
  const products = useStore((state) => state.products);
  const clearCart = useStore((state) => state.clearCart);
  const token = useAuthStore((state) => state.token);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const orderCreatedRef = useRef(false);

  useEffect(() => {
    console.log('PaymentSuccess mounted. OrderId:', orderId, 'Status:', paymentStatus);

    if (!orderId) {
      console.error('No orderId in URL');
      setError('No order ID provided');
      setLoading(false);
      return;
    }

    // If payment status is failed, show error immediately
    if (paymentStatus === 'failed' || paymentStatus === 'declined' || paymentStatus === 'fail') {
      setError('Your payment was declined by Bank of Georgia. Please try again with a different payment method or contact your bank.');
      setLoading(false);
      return;
    }

    // For successful BoG payments, just clear cart and show success
    // (Order was already created on backend before redirect)
    if (paymentStatus === 'success' && orderId) {
      console.log('BoG payment successful, clearing cart');
      clearCart();
      sessionStorage.removeItem('checkoutData');
      setOrder({
        id: orderId,
        order_number: orderId,
        status: 'processing',
        createdAt: new Date().toISOString(),
      });
      setLoading(false);
      return;
    }

    // Only create order once (for test payments or other flows)
    if (orderCreatedRef.current) {
      console.log('Order already created, skipping');
      return;
    }

    const createOrder = async () => {
      try {
        if (!token) {
          console.warn('No authentication token found');
          setError('Authentication required to create order');
          setLoading(false);
          return;
        }

        // Retrieve checkout data from sessionStorage
        const checkoutDataStr = sessionStorage.getItem('checkoutData');
        if (!checkoutDataStr) {
          console.warn('No checkout data found in sessionStorage');
          setError('Checkout data not found. Please try again.');
          setLoading(false);
          return;
        }

        const checkoutData = JSON.parse(checkoutDataStr);
        const { formData, subtotal, shipping, total } = checkoutData;

        // Build shipping address from form data
        const shippingAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}, ${formData.country}`;

        // Transform cart items to match order creation endpoint format
        // Endpoint expects: product_id, size, color, quantity, price
        const orderItems = cart.map((item) => {
          // Get current price from products store if available
          const product = products.find(p => p.id === item.id);
          const currentPrice = product ? parseFloat(product.price) : parseFloat(item.price);
          const discountPrice = product?.discount_price ? parseFloat(product.discount_price) : 0;
          const displayPrice = discountPrice > 0 ? discountPrice : currentPrice;

          return {
            product_id: item.id,
            color_id: item.color_id || null, // Color ID for inventory tracking
            size: item.size || 'default', // Use stored size or default if not available
            quantity: item.quantity || 1,
            price: displayPrice,
          };
        });

        console.log('Creating order with items:', orderItems);

        // Create order in database
        const response = await fetch(`${API_BASE_URL}/api/orders/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            items: orderItems,
            subtotal: subtotal,
            shipping: shipping,
            total: total,
            payment_method: 'bog',
            shipping_address: shippingAddress,
            transaction_id: transactionId, // Store BoG transaction ID
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          console.error('Order creation failed:', data);
          setError(data.error || 'Failed to create order');
          setLoading(false);
          return;
        }

        console.log('Order created successfully:', data);

        // Mark order as created to prevent duplicate calls
        orderCreatedRef.current = true;

        // Clear checkout data from sessionStorage
        sessionStorage.removeItem('checkoutData');

        // Set order details for display
        setOrder({
          id: orderId,
          order_number: data.order_number,
          status: 'confirmed',
          createdAt: new Date().toISOString(),
          total: total,
          transaction_id: transactionId,
        });

        // Clear the cart since payment was successful
        clearCart();

        setLoading(false);
      } catch (err) {
        console.error('Error processing payment:', err);
        setError('Failed to process payment. Please contact support.');
        setLoading(false);
      }
    };

    createOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token, paymentStatus]);

  if (loading) {
    return (
      <div className="payment-success-page">
        <div className="success-container">
          <div className="loading">Processing your payment...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-page">
        <div className="success-container">
          <div className="error-box">
            <h2>Payment Error</h2>
            <p>{error}</p>
            <Link to="/products" className="btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-page">
      <div className="success-container">
        <div className="success-box">
          <div className="success-icon">✓</div>
          <h1>Payment Successful!</h1>
          <p className="thank-you">Thank you for your purchase!</p>

          <div className="order-details">
            <div className="detail-row highlight">
              <span className="label">Order Number:</span>
              <span className="value order-number">{order?.order_number || `Order #${orderId}`}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className="value status-confirmed">Confirmed</span>
            </div>
            <div className="detail-row">
              <span className="label">Date:</span>
              <span className="value">
                {order?.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Today'}
              </span>
            </div>
          </div>

          <div className="next-steps">
            <h3>What's Next?</h3>
            <ul>
              <li>We'll send you a confirmation email shortly</li>
              <li>Track your order in your profile</li>
              <li>You'll receive shipping updates via email</li>
            </ul>
          </div>

          <div className="action-buttons">
            <Link to="/profile" className="btn-primary">
              View Order Details
            </Link>
            <Link to="/products" className="btn-secondary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
